import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { LOD_LEVELS, getAutoUpgradeLOD } from '../lib/heatmapUtils';
import MultiSelect from './MultiSelect';
import { HeatmapViewer } from './Heatmaps';

/**
 * Compare mode component for side-by-side heatmap comparison
 * Shows two synchronized viewers with different CL data
 */
function CompareMode({ area, api, daysFilter, startDate = null, endDate = null, colorMetric, activeEvaluator = null, mapConfig, filters, onExit, initialCameraState = null }) {
  // Selection state for left and right sides (now supports multiple CLs)
  const [leftSelection, setLeftSelection] = useState({ branch: null, cls: [] });
  const [rightSelection, setRightSelection] = useState({ branch: null, cls: [] });
  const [availableCLs, setAvailableCLs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data for each side
  const [leftData, setLeftData] = useState(null);
  const [rightData, setRightData] = useState(null);
  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);

  // LOD progress tracking for each side
  const [leftLodProgress, setLeftLodProgress] = useState({});
  const [rightLodProgress, setRightLodProgress] = useState({});

  // LOD cache refs for each side
  const leftLodCacheRef = useRef(new Map());
  const rightLodCacheRef = useRef(new Map());

  // Refs to track which LOD loads have been initiated (to prevent duplicate requests)
  const leftLoadInitiatedRef = useRef({});
  const rightLoadInitiatedRef = useRef({});

  // Shared state - initialize with camera state from main viewer if available
  const [sharedLOD, setSharedLOD] = useState(0);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const sharedCameraState = useRef({
    position: initialCameraState?.position || null,
    target: initialCameraState?.target || null,
    frustumSize: initialCameraState?.frustumSize || null,
    zoom: initialCameraState?.zoom || null,
    lastUpdateTime: initialCameraState ? performance.now() : 0,
    lastUpdateSource: initialCameraState ? 'initial' : null
  });

  // Refs for viewers
  const leftViewerRef = useRef(null);
  const rightViewerRef = useRef(null);

  // Load available CLs on mount
  useEffect(() => {
    async function loadCLs() {
      setLoading(true);
      setError(null);
      try {
        const branch = filters.branches?.[0] || null;
        const result = await api.getBigQueryHeatmapCLsForBranch(area.name, daysFilter, branch, startDate, endDate);
        const cls = result.cls || [];
        setAvailableCLs(cls);

        // Default selection: most recent CL on left, previous CL on right
        if (cls.length >= 2) {
          setLeftSelection({ branch, cls: [cls[0].cl] });
          setRightSelection({ branch, cls: [cls[1].cl] });
        } else if (cls.length === 1) {
          setLeftSelection({ branch, cls: [cls[0].cl] });
          setRightSelection({ branch, cls: [cls[0].cl] });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadCLs();
  }, [api, area.name, daysFilter, startDate, endDate, filters.branches]);

  // Clear left LOD progress, cache, and initiated tracking when CLs change
  useEffect(() => {
    setLeftLodProgress({});
    leftLodCacheRef.current.clear();
    leftLoadInitiatedRef.current = {};
  }, [leftSelection.cls.join(',')]);

  // Load data for left side when selection changes
  useEffect(() => {
    if (leftSelection.cls.length === 0) return;

    // Check if already cached (use ref to avoid dependency)
    const cached = leftLodCacheRef.current.get(sharedLOD);
    if (cached) {
      setLeftData(cached);
      setLeftLodProgress(prev => {
        if (prev[sharedLOD] === 'loaded') return prev;
        return { ...prev, [sharedLOD]: 'loaded' };
      });
      return;
    }

    async function loadLeftData() {
      setLeftLoading(true);
      setLeftLodProgress(prev => ({ ...prev, [sharedLOD]: 'loading' }));
      try {
        const result = await api.getBigQueryHeatmapDataLOD(area.name, daysFilter, {
          ...filters,
          cls: leftSelection.cls
        }, {
          cellSize: LOD_LEVELS[sharedLOD]?.cellSize,
          runLimit: 30
        }, startDate, endDate);

        // Transform to capturePoints format
        const capturePoints = (result.cells || result.capturePoints || []).map(cell => ({
          position: cell.position,
          rotation: cell.rotation,
          framerate: cell.fps?.min ?? cell.framerate,
          gpuTime: cell.gpuTime?.avg ?? cell.gpuTime,
          cpuTime: cell.cpuTime?.avg ?? cell.cpuTime,
          drawCalls: cell.drawCalls?.avg ?? cell.drawCalls,
          batches: cell.batches?.avg ?? cell.batches,
          triangles: cell.triangles?.avg ?? cell.triangles,
          vertices: cell.vertices?.avg ?? cell.vertices,
          _isLODCell: result.cellSize != null,
          _cellSize: result.cellSize,
          _sampleCount: cell.sampleCount || 1,
          _devices: cell.devices || [],
          _fpsStats: cell.fps,
          _source: cell._source || `L_${cell.gridX || 0}_${cell.gridZ || 0}`,
          _device: cell._device || cell.devices?.join(', ') || 'Unknown',
          _quality: cell._quality || 'Aggregated'
        }));

        const data = {
          ...result,
          capturePoints,
          totalPoints: capturePoints.length,
          _totalSamples: result.totalSamples || capturePoints.length,
          _cellSize: result.cellSize
        };

        // Cache the data
        leftLodCacheRef.current.set(sharedLOD, data);
        setLeftData(data);
        setLeftLodProgress(prev => ({ ...prev, [sharedLOD]: 'loaded' }));
      } catch (err) {
        console.error('Failed to load left data:', err);
        setLeftData(null);
        setLeftLodProgress(prev => ({ ...prev, [sharedLOD]: 'error' }));
      } finally {
        setLeftLoading(false);
      }
    }
    loadLeftData();
  }, [api, area.name, daysFilter, startDate, endDate, filters, leftSelection.cls, sharedLOD]);

  // Clear right LOD progress, cache, and initiated tracking when CLs change
  useEffect(() => {
    setRightLodProgress({});
    rightLodCacheRef.current.clear();
    rightLoadInitiatedRef.current = {};
  }, [rightSelection.cls.join(',')]);

  // Load data for right side when selection changes
  useEffect(() => {
    if (rightSelection.cls.length === 0) return;

    // Check if already cached (use ref to avoid dependency)
    const cached = rightLodCacheRef.current.get(sharedLOD);
    if (cached) {
      setRightData(cached);
      setRightLodProgress(prev => {
        if (prev[sharedLOD] === 'loaded') return prev;
        return { ...prev, [sharedLOD]: 'loaded' };
      });
      return;
    }

    async function loadRightData() {
      setRightLoading(true);
      setRightLodProgress(prev => ({ ...prev, [sharedLOD]: 'loading' }));
      try {
        const result = await api.getBigQueryHeatmapDataLOD(area.name, daysFilter, {
          ...filters,
          cls: rightSelection.cls
        }, {
          cellSize: LOD_LEVELS[sharedLOD]?.cellSize,
          runLimit: 30
        }, startDate, endDate);

        // Transform to capturePoints format
        const capturePoints = (result.cells || result.capturePoints || []).map(cell => ({
          position: cell.position,
          rotation: cell.rotation,
          framerate: cell.fps?.min ?? cell.framerate,
          gpuTime: cell.gpuTime?.avg ?? cell.gpuTime,
          cpuTime: cell.cpuTime?.avg ?? cell.cpuTime,
          drawCalls: cell.drawCalls?.avg ?? cell.drawCalls,
          batches: cell.batches?.avg ?? cell.batches,
          triangles: cell.triangles?.avg ?? cell.triangles,
          vertices: cell.vertices?.avg ?? cell.vertices,
          _isLODCell: result.cellSize != null,
          _cellSize: result.cellSize,
          _sampleCount: cell.sampleCount || 1,
          _devices: cell.devices || [],
          _fpsStats: cell.fps,
          _source: cell._source || `R_${cell.gridX || 0}_${cell.gridZ || 0}`,
          _device: cell._device || cell.devices?.join(', ') || 'Unknown',
          _quality: cell._quality || 'Aggregated'
        }));

        const data = {
          ...result,
          capturePoints,
          totalPoints: capturePoints.length,
          _totalSamples: result.totalSamples || capturePoints.length,
          _cellSize: result.cellSize
        };

        // Cache the data
        rightLodCacheRef.current.set(sharedLOD, data);
        setRightData(data);
        setRightLodProgress(prev => ({ ...prev, [sharedLOD]: 'loaded' }));
      } catch (err) {
        console.error('Failed to load right data:', err);
        setRightData(null);
        setRightLodProgress(prev => ({ ...prev, [sharedLOD]: 'error' }));
      } finally {
        setRightLoading(false);
      }
    }
    loadRightData();
  }, [api, area.name, daysFilter, startDate, endDate, filters, rightSelection.cls, sharedLOD]);

  // Helper to transform cells to capturePoints format
  const transformCells = useCallback((result) => {
    return (result.cells || result.capturePoints || []).map(cell => ({
      position: cell.position,
      rotation: cell.rotation,
      framerate: cell.fps?.min ?? cell.framerate,
      gpuTime: cell.gpuTime?.avg ?? cell.gpuTime,
      cpuTime: cell.cpuTime?.avg ?? cell.cpuTime,
      drawCalls: cell.drawCalls?.avg ?? cell.drawCalls,
      batches: cell.batches?.avg ?? cell.batches,
      triangles: cell.triangles?.avg ?? cell.triangles,
      vertices: cell.vertices?.avg ?? cell.vertices,
      _isLODCell: result.cellSize != null,
      _cellSize: result.cellSize,
      _sampleCount: cell.sampleCount || 1,
      _devices: cell.devices || [],
      _fpsStats: cell.fps,
      _source: cell._source || `L_${cell.gridX || 0}_${cell.gridZ || 0}`,
      _device: cell._device || cell.devices?.join(', ') || 'Unknown',
      _quality: cell._quality || 'Aggregated'
    }));
  }, []);

  // Progressive loading - load LOD1 and LOD2 in background after LOD0 is loaded
  useEffect(() => {
    if (leftSelection.cls.length === 0 || !leftData) return;

    async function loadProgressiveLOD(lodLevel) {
      const loadKey = `${leftSelection.cls.join(',')}-${lodLevel}`;
      // Skip if already initiated or cached
      if (leftLoadInitiatedRef.current[loadKey] || leftLodCacheRef.current.has(lodLevel)) return;
      leftLoadInitiatedRef.current[loadKey] = true;

      setLeftLodProgress(prev => ({ ...prev, [lodLevel]: 'loading' }));
      console.log(`[CompareMode] Background loading left LOD ${lodLevel}...`);

      try {
        const result = await api.getBigQueryHeatmapDataLOD(area.name, daysFilter, {
          ...filters,
          cls: leftSelection.cls
        }, {
          cellSize: LOD_LEVELS[lodLevel]?.cellSize,
          runLimit: 30
        }, startDate, endDate);

        const capturePoints = transformCells(result);
        const data = {
          ...result,
          capturePoints,
          totalPoints: capturePoints.length,
          _totalSamples: result.totalSamples || capturePoints.length,
          _cellSize: result.cellSize
        };

        leftLodCacheRef.current.set(lodLevel, data);
        setLeftLodProgress(prev => ({ ...prev, [lodLevel]: 'loaded' }));
        console.log(`[CompareMode] left LOD ${lodLevel} loaded: ${capturePoints.length} points`);
      } catch (err) {
        console.error(`[CompareMode] Failed to load left LOD ${lodLevel}:`, err);
        setLeftLodProgress(prev => ({ ...prev, [lodLevel]: 'error' }));
      }
    }

    // Load LOD1 first, then LOD2 sequentially to avoid overwhelming the server
    loadProgressiveLOD(1).then(() => loadProgressiveLOD(2));
  }, [api, area.name, daysFilter, startDate, endDate, filters, leftSelection.cls, leftData, transformCells]);

  // Progressive loading for right side
  useEffect(() => {
    if (rightSelection.cls.length === 0 || !rightData) return;

    async function loadProgressiveLOD(lodLevel) {
      const loadKey = `${rightSelection.cls.join(',')}-${lodLevel}`;
      // Skip if already initiated or cached
      if (rightLoadInitiatedRef.current[loadKey] || rightLodCacheRef.current.has(lodLevel)) return;
      rightLoadInitiatedRef.current[loadKey] = true;

      setRightLodProgress(prev => ({ ...prev, [lodLevel]: 'loading' }));
      console.log(`[CompareMode] Background loading right LOD ${lodLevel}...`);

      try {
        const result = await api.getBigQueryHeatmapDataLOD(area.name, daysFilter, {
          ...filters,
          cls: rightSelection.cls
        }, {
          cellSize: LOD_LEVELS[lodLevel]?.cellSize,
          runLimit: 30
        }, startDate, endDate);

        const capturePoints = transformCells(result);
        const data = {
          ...result,
          capturePoints,
          totalPoints: capturePoints.length,
          _totalSamples: result.totalSamples || capturePoints.length,
          _cellSize: result.cellSize
        };

        rightLodCacheRef.current.set(lodLevel, data);
        setRightLodProgress(prev => ({ ...prev, [lodLevel]: 'loaded' }));
        console.log(`[CompareMode] right LOD ${lodLevel} loaded: ${capturePoints.length} points`);
      } catch (err) {
        console.error(`[CompareMode] Failed to load right LOD ${lodLevel}:`, err);
        setRightLodProgress(prev => ({ ...prev, [lodLevel]: 'error' }));
      }
    }

    // Load LOD1 first, then LOD2 sequentially
    loadProgressiveLOD(1).then(() => loadProgressiveLOD(2));
  }, [api, area.name, daysFilter, startDate, endDate, filters, rightSelection.cls, rightData, transformCells]);

  // Auto-upgrade LOD when point count is below ideal (uses shared utility)
  // Depends on lodProgress so it re-runs when background downloads complete
  useEffect(() => {
    if (!leftData || !rightData) return;

    const totalPoints = (leftData.totalPoints || 0) + (rightData.totalPoints || 0);
    const upgrade = getAutoUpgradeLOD(sharedLOD, totalPoints, [leftLodCacheRef, rightLodCacheRef]);

    if (upgrade) {
      console.log(`[CompareMode] Auto-upgrading LOD ${sharedLOD} -> ${upgrade.level} (${totalPoints} -> ${upgrade.points} points)`);
      setLeftData(leftLodCacheRef.current.get(upgrade.level));
      setRightData(rightLodCacheRef.current.get(upgrade.level));
      setSharedLOD(upgrade.level);
    }
  }, [leftData, rightData, sharedLOD, leftLodProgress, rightLodProgress]);

  // Helper to get aggregated stats for multiple CLs
  const getAggregatedCLStats = useCallback((clIds) => {
    const cls = clIds.map(id => availableCLs.find(c => c.cl === id)).filter(Boolean);
    if (cls.length === 0) return null;

    // Aggregate by weighted average based on sample count
    const totalSamples = cls.reduce((sum, cl) => sum + (cl.sampleCount || 1), 0);

    const aggregate = (key) => {
      const weighted = cls.reduce((sum, cl) => {
        const val = cl[key] || 0;
        const weight = cl.sampleCount || 1;
        return sum + val * weight;
      }, 0);
      return totalSamples > 0 ? weighted / totalSamples : 0;
    };

    return {
      avgFps: aggregate('avgFps'),
      lowFps: Math.min(...cls.map(cl => cl.lowFps || 0).filter(v => v > 0)) || 0,
      avgGpuTime: aggregate('avgGpuTime'),
      avgCpuTime: aggregate('avgCpuTime'),
      avgDrawCalls: aggregate('avgDrawCalls'),
      avgBatches: aggregate('avgBatches'),
      avgTriangles: aggregate('avgTriangles'),
      avgVertices: aggregate('avgVertices'),
      sampleCount: totalSamples,
      runCount: cls.reduce((sum, cl) => sum + (cl.runCount || 0), 0),
      devices: [...new Set(cls.flatMap(cl => cl.devices || []))],
      qualities: [...new Set(cls.flatMap(cl => cl.qualities || []))]
    };
  }, [availableCLs]);

  // Calculate matched device/quality pairs
  const matchedPairs = useMemo(() => {
    const leftStats = getAggregatedCLStats(leftSelection.cls);
    const rightStats = getAggregatedCLStats(rightSelection.cls);
    if (!leftStats || !rightStats) return [];

    const leftPairs = new Set();
    (leftStats.devices || []).forEach(d => {
      (leftStats.qualities || []).forEach(q => {
        leftPairs.add(`${d}|${q}`);
      });
    });

    const matched = [];
    (rightStats.devices || []).forEach(d => {
      (rightStats.qualities || []).forEach(q => {
        if (leftPairs.has(`${d}|${q}`)) {
          matched.push({ device: d, quality: q });
        }
      });
    });

    return matched;
  }, [getAggregatedCLStats, leftSelection.cls, rightSelection.cls]);

  // Calculate difference summary
  const differenceSummary = useMemo(() => {
    const leftStats = getAggregatedCLStats(leftSelection.cls);
    const rightStats = getAggregatedCLStats(rightSelection.cls);
    if (!leftStats || !rightStats) return null;

    const metrics = [
      { key: 'avgFps', label: 'FPS', higherIsBetter: true, format: 'fps' },
      { key: 'lowFps', label: 'Low FPS', higherIsBetter: true, format: 'fps' },
      { key: 'avgGpuTime', label: 'GPU', higherIsBetter: false, format: 'ms' },
      { key: 'avgCpuTime', label: 'CPU', higherIsBetter: false, format: 'ms' },
      { key: 'avgDrawCalls', label: 'Draw Calls', higherIsBetter: false, format: 'int' },
      { key: 'avgBatches', label: 'Batches', higherIsBetter: false, format: 'int' },
      { key: 'avgTriangles', label: 'Triangles', higherIsBetter: false, format: 'k' },
      { key: 'avgVertices', label: 'Vertices', higherIsBetter: false, format: 'k' }
    ];

    return metrics.map(metric => {
      const leftVal = leftStats[metric.key] || 0;
      const rightVal = rightStats[metric.key] || 0;
      const diff = leftVal - rightVal;
      const percentDiff = rightVal !== 0 ? ((diff / rightVal) * 100).toFixed(1) : 0;

      let status = 'neutral';
      if (Math.abs(diff) > 0.5) {
        if (metric.higherIsBetter) {
          status = diff > 0 ? 'improved' : 'regressed';
        } else {
          status = diff < 0 ? 'improved' : 'regressed';
        }
      }

      return {
        ...metric,
        leftVal,
        rightVal,
        diff,
        percentDiff,
        status
      };
    });
  }, [getAggregatedCLStats, leftSelection.cls, rightSelection.cls]);

  // Handle LOD change for both viewers - use point budget to determine optimal LOD
  const handleLODChange = useCallback(async (newLOD) => {
    // Calculate optimal LOD based on point budget (may override zoom-based request)
    const totalPoints = (leftData?.totalPoints || 0) + (rightData?.totalPoints || 0);
    const upgrade = getAutoUpgradeLOD(newLOD, totalPoints, [leftLodCacheRef, rightLodCacheRef]);
    const targetLOD = upgrade ? upgrade.level : newLOD;

    // Skip if already at target LOD
    if (targetLOD === sharedLOD) return;

    console.log(`[CompareMode] LOD change: ${sharedLOD} -> ${targetLOD} (requested: ${newLOD}, reason: ${upgrade ? 'budget' : 'zoom'})`);

    // Check if both sides have cached data for target LOD
    const leftCached = leftLodCacheRef.current.get(targetLOD);
    const rightCached = rightLodCacheRef.current.get(targetLOD);

    if (leftCached) {
      setLeftData(leftCached);
    }

    if (rightCached) {
      setRightData(rightCached);
    }

    setSharedLOD(targetLOD);
  }, [sharedLOD, leftData, rightData]);

  // Get CL info helper
  const getCLInfo = (cl) => availableCLs.find(c => c.cl === cl);

  // Calculate combined bounds from both data sets so both viewers render the same spatial area
  // Must be before early returns to satisfy React hooks rules
  const sharedBounds = useMemo(() => {
    if (!leftData && !rightData) return null;

    // Compute from points with percentile-based outlier filtering
    const xVals = [];
    const zVals = [];
    const collectPoints = (data) => {
      if (!data?.capturePoints) return;
      for (const p of data.capturePoints) {
        if (p.position) {
          xVals.push(-p.position.x);
          zVals.push(p.position.z);
        }
      }
    };
    collectPoints(leftData);
    collectPoints(rightData);

    if (xVals.length === 0) return null;

    const filterOutliers = (values) => {
      if (values.length < 4) return { min: Math.min(...values), max: Math.max(...values) };
      const sorted = [...values].sort((a, b) => a - b);
      const lo = sorted[Math.floor(sorted.length * 0.05)];
      const hi = sorted[Math.floor(sorted.length * 0.95)];
      const span = hi - lo || 1;
      return { min: lo - span * 0.2, max: hi + span * 0.2 };
    };

    const xRange = filterOutliers(xVals);
    const zRange = filterOutliers(zVals);

    const bounds = {
      minX: xRange.min, maxX: xRange.max,
      minY: Infinity, maxY: -Infinity,
      minZ: zRange.min, maxZ: zRange.max
    };

    const addY = (data) => {
      if (!data?.capturePoints) return;
      for (const p of data.capturePoints) {
        if (p.position) {
          bounds.minY = Math.min(bounds.minY, p.position.y);
          bounds.maxY = Math.max(bounds.maxY, p.position.y);
        }
      }
    };
    addY(leftData);
    addY(rightData);
    if (!isFinite(bounds.minY)) { bounds.minY = 0; bounds.maxY = 100; }

    return bounds;
  }, [leftData, rightData]);

  if (loading) {
    return (
      <div className="compare-mode">
        <div className="compare-loading">
          <div className="loading-spinner"></div>
          <span>Loading CLs for comparison...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="compare-mode">
        <div className="compare-error">
          <span>Failed to load CLs: {error}</span>
          <button onClick={onExit}>Exit Compare</button>
        </div>
      </div>
    );
  }

  if (availableCLs.length < 1) {
    return (
      <div className="compare-mode">
        <div className="compare-error">
          <span>No CLs available for comparison in this time range.</span>
          <button onClick={onExit}>Exit Compare</button>
        </div>
      </div>
    );
  }

  const leftCLInfo = getAggregatedCLStats(leftSelection.cls);
  const rightCLInfo = getAggregatedCLStats(rightSelection.cls);

  return (
    <div className="compare-mode">
      <div className="compare-mode-header">
        <span className="compare-mode-title">Compare Mode</span>
        <div className="compare-mode-controls">
          <label className="compare-unmatched-toggle">
            <input
              type="checkbox"
              checked={showUnmatched}
              onChange={(e) => setShowUnmatched(e.target.checked)}
            />
            Show all device/quality runs
            {matchedPairs.length > 0 && (
              <span className="compare-unmatched-count">
                ({matchedPairs.length} matched)
              </span>
            )}
          </label>
          <div className="sync-indicator">
            <span className="sync-indicator-dot"></span>
            <span>Camera Synced</span>
          </div>
          <button className="compare-mode-exit" onClick={onExit}>
            Exit Compare
          </button>
        </div>
      </div>

      {/* Difference Summary */}
      {differenceSummary && (
        <div className="difference-summary">
          <div className="difference-summary-title">Performance Difference (New vs Base)</div>
          {differenceSummary.map(metric => {
            // Format the difference value based on metric type
            let diffDisplay;
            const sign = metric.diff > 0 ? '+' : '';
            switch (metric.format) {
              case 'ms':
                diffDisplay = `${sign}${metric.diff.toFixed(2)}ms`;
                break;
              case 'k':
                diffDisplay = Math.abs(metric.diff) >= 1000
                  ? `${sign}${(metric.diff / 1000).toFixed(1)}k`
                  : `${sign}${metric.diff.toFixed(0)}`;
                break;
              case 'int':
                diffDisplay = `${sign}${metric.diff.toFixed(0)}`;
                break;
              default:
                diffDisplay = `${sign}${metric.diff.toFixed(1)}`;
            }
            return (
              <div key={metric.key} className={`diff-item delta-${metric.status}`}>
                <span className="diff-label">{metric.label}</span>
                <span className="diff-delta">{diffDisplay}</span>
                <span className="diff-percent">({metric.percentDiff}%)</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Split Viewers */}
      <div className="compare-viewers">
        {/* Left Viewer */}
        <div className="compare-viewer-panel">
          <div className="compare-viewer-header">
            <span className="compare-viewer-label left">NEW</span>
            <div className="compare-selector">
              <MultiSelect
                label=""
                options={availableCLs.map(cl => cl.cl)}
                selected={leftSelection.cls}
                onChange={(cls) => setLeftSelection(prev => ({ ...prev, cls }))}
                placeholder="Select CLs..."
                renderOption={(cl) => {
                  const info = getCLInfo(cl);
                  return `CL ${cl} - ${info?.avgFps?.toFixed(1) || '?'} AVG`;
                }}
              />
              {leftCLInfo && (
                <span className="cl-badge left">
                  {leftSelection.cls.length} CL{leftSelection.cls.length !== 1 ? 's' : ''}, {leftCLInfo.runCount} runs
                </span>
              )}
            </div>
          </div>
          <div className="compare-viewer-content" ref={leftViewerRef}>
            {leftLoading ? (
              <div className="compare-loading">
                <div className="loading-spinner"></div>
                <span>Loading...</span>
              </div>
            ) : leftData ? (
              <HeatmapViewer
                data={leftData}
                containerRef={leftViewerRef}
                mapConfig={mapConfig}
                selectedPoints={[]}
                setSelectedPoints={() => {}}
                colorMetric={colorMetric}
                activeEvaluator={activeEvaluator}
                currentLOD={sharedLOD}
                onLODChange={handleLODChange}
                lodProgress={leftLodProgress}
                syncCameraState={sharedCameraState}
                viewerId="left"
                initialCameraState={initialCameraState}
                sharedBounds={sharedBounds}
              />
            ) : (
              <div className="compare-error">No data for selected CL</div>
            )}
          </div>
        </div>

        {/* Right Viewer */}
        <div className="compare-viewer-panel">
          <div className="compare-viewer-header">
            <span className="compare-viewer-label right">BASE</span>
            <div className="compare-selector">
              <MultiSelect
                label=""
                options={availableCLs.map(cl => cl.cl)}
                selected={rightSelection.cls}
                onChange={(cls) => setRightSelection(prev => ({ ...prev, cls }))}
                placeholder="Select CLs..."
                renderOption={(cl) => {
                  const info = getCLInfo(cl);
                  return `CL ${cl} - ${info?.avgFps?.toFixed(1) || '?'} AVG`;
                }}
              />
              {rightCLInfo && (
                <span className="cl-badge right">
                  {rightSelection.cls.length} CL{rightSelection.cls.length !== 1 ? 's' : ''}, {rightCLInfo.runCount} runs
                </span>
              )}
            </div>
          </div>
          <div className="compare-viewer-content" ref={rightViewerRef}>
            {rightLoading ? (
              <div className="compare-loading">
                <div className="loading-spinner"></div>
                <span>Loading...</span>
              </div>
            ) : rightData ? (
              <HeatmapViewer
                data={rightData}
                containerRef={rightViewerRef}
                mapConfig={mapConfig}
                selectedPoints={[]}
                setSelectedPoints={() => {}}
                colorMetric={colorMetric}
                activeEvaluator={activeEvaluator}
                currentLOD={sharedLOD}
                onLODChange={null}
                lodProgress={rightLodProgress}
                syncCameraState={sharedCameraState}
                viewerId="right"
                initialCameraState={initialCameraState}
                sharedBounds={sharedBounds}
              />
            ) : (
              <div className="compare-error">No data for selected CL</div>
            )}
          </div>
        </div>
      </div>

      {/* Combined Performance Chart */}
      {leftCLInfo && rightCLInfo && (
        <div className="compare-charts">
          <div className="compare-charts-title">Performance Comparison</div>
          <div className="compare-chart">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={[
                {
                  name: leftSelection.cls.length === 1 ? `CL ${leftSelection.cls[0]}` : `${leftSelection.cls.length} CLs (New)`,
                  avgFps: leftCLInfo.avgFps,
                  lowFps: leftCLInfo.lowFps,
                  side: 'left'
                },
                {
                  name: rightSelection.cls.length === 1 ? `CL ${rightSelection.cls[0]}` : `${rightSelection.cls.length} CLs (Base)`,
                  avgFps: rightCLInfo.avgFps,
                  lowFps: rightCLInfo.lowFps,
                  side: 'right'
                }
              ]} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 'auto']} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <ReferenceLine y={45} stroke="#10b981" strokeDasharray="5 5" />
                <ReferenceLine y={30} stroke="#f59e0b" strokeDasharray="5 5" />
                <Line type="monotone" dataKey="avgFps" name="AVG FPS" stroke="#3b82f6" strokeWidth={2} dot={{ r: 5 }} />
                <Line type="monotone" dataKey="lowFps" name="LOW FPS" stroke="#ef4444" strokeWidth={2} dot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompareMode;
